import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { EnumGlobalRoutes } from '@/app/router/routerConstants'
import DeleteIcon from '@/assets/img/delete-icon.svg?react'
import DotsSvg from '@/assets/img/dots.svg'
import EditIcon from '@/assets/img/edit-pan.svg?react'
import TeamIcon from '@/assets/img/ico-add-to-team.svg?react'
import AddIcon from '@/assets/img/ico-add.svg?react'
import ChatIcon from '@/assets/img/ico-chat.svg?react'
import SocialIcon from '@/assets/img/ico-social.svg?react'
import ProfileIcon from '@/assets/img/ico-view-profile.svg?react'
import Button from '@/compoments/common/Button'
import Dropdown from '@/compoments/common/Dropdown'
import Pagination from '@/compoments/common/Pagination'
import { PillStatus } from '@/compoments/common/PillStatus'
import SelectBox from '@/compoments/common/SelectBox'
import UserPart from '@/compoments/common/UserPart'
import DropdownList from '@/compoments/DropdownList'
import AddUserModal from '@/compoments/modals/AddUserModal'
import PageLoader from '@/compoments/PageLoader'
import { useAppSelector } from '@/hooks/redux'
import { useCreateChatByUserMutation } from '@/services/chats/chats'
import {
    useDeleteUserMutation,
    useGetAllUsersQuery,
} from '@/services/tenant/users'
import type { ITenantUser } from '@/services/types'
import type { IOptionProps, UserRoleEnum } from '@/types'
import { ROLE_LABELS } from '@/types'
import { optionsOnlineStatus, optionsRole } from '@/utils/selectsOptionsConst'
import AddToTeam from '@/compoments/modals/AddToTeam'
import AddToWorkGroup from '@/compoments/modals/AddToWorkGroup'

export const AdminUserManagement = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const { data, isLoading } = useGetAllUsersQuery(searchParams.toString())
    const [deleteUser] = useDeleteUserMutation()
    const [addUserModal, setAddUserModal] = useState<boolean>(false)
    const [addUserToTeamModal, setAddUserToTeamModal] = useState<boolean>(false)
    const [addUserToWorkGroupModal, setAddUserToWorkGroupModal] =
        useState<boolean>(false)
    const [editUserId, setEditUserId] = useState<string | null>(null)
    const [createChatByUser] = useCreateChatByUserMutation()
    const navigate = useNavigate()
    const onlineUsers = useAppSelector(state => state.app.onlineUsers)

    const filterHandler = (
        name: string,
        value: string | number | undefined,
    ) => {
        setSearchParams((prev: URLSearchParams) => {
            prev.set('page', '1')
            if (value) {
                prev.set(name, String(value))
            } else {
                prev.delete(name)
            }
            return prev
        })
    }
    const editHandler = (id: number) => {
        setEditUserId(String(id))
        setAddUserModal(true)
    }
    const deleteHandler = async (id: number) => {
        const { toast } = await import('react-toastify')
        try {
            const res = await deleteUser(id).unwrap()
            if (res?.success) {
                toast.success('Deleted successfully!')
            } else {
                toast.error(res?.message || 'Something went wrong')
            }
        } catch (error) {
            console.error('rejected', error)
        }
    }

    const startChatHandler = async (user: ITenantUser) => {
        try {
            const formData = new FormData()
            formData.append('name', user.fullName)
            formData.append('content', `Hey ${user.fullName}`)

            const res = await createChatByUser({
                id: user.id,
                data: formData,
            }).unwrap()

            if (res.success) {
                toast.success(res.message || 'Chat created successfully')
                navigate(`${EnumGlobalRoutes.CHAT}/${res.data.id}`)
            } else {
                toast.error(res.message || 'Something went wrong')
            }
        } catch (error) {
            console.error('Failed to create chat', error)
        }
    }

    const pageData = useMemo(() => {
        return data?.data.map((user: ITenantUser) => {
            return {
                ...user,
                status:
                    onlineUsers.find(u => u.id === user.id)?.status ||
                    user.status,
            }
        })
    }, [data, onlineUsers])

    if (isLoading) return <PageLoader />

    return (
        <main className="content">
            <div className="px-container">
                <div className="table-group">
                    <div className="table-header flex items-center justify-between">
                        <div className="table-tools">
                            <SelectBox
                                options={optionsOnlineStatus as IOptionProps[]}
                                placeholder="Status"
                                onChange={option =>
                                    filterHandler('status', option?.value)
                                }
                                isClearable={true}
                            />
                            <SelectBox
                                options={optionsRole}
                                placeholder="Role"
                                onChange={option =>
                                    filterHandler('roles', option?.value)
                                }
                                isClearable={true}
                            />
                        </div>

                        <Button
                            size="sm"
                            variant="primary-gradient"
                            onClick={() => setAddUserModal(true)}
                        >
                            <AddIcon />
                            <span className="text--xs">Create User</span>
                        </Button>
                    </div>

                    <div className="table-body table-responsive p-0">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Full Name</th>
                                    {/* <th>Phone</th> */}
                                    <th style={{ width: '100%' }}>Role</th>
                                    <th style={{ width: '80px' }}>Status</th>
                                    <th
                                        style={{ width: '10px' }}
                                        className="text-right"
                                    >
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {pageData && pageData.length > 0 ? (
                                    pageData.map((user: ITenantUser) => (
                                        <tr key={user.id}>
                                            <td>
                                                <UserPart
                                                    src={user.photo}
                                                    name={user.fullName}
                                                    email={user.email}
                                                />
                                            </td>
                                            {/* <td>{user.phone}</td> */}
                                            <td>
                                                {user.roles
                                                    .map(r => ROLE_LABELS[r])
                                                    .join(', ')}
                                            </td>
                                            <td>
                                                <PillStatus
                                                    status={
                                                        user.status as string
                                                    }
                                                />
                                            </td>
                                            <td className="text-right sticky-end">
                                                <Dropdown
                                                    closeSelf={true}
                                                    dropdownClasses="flex flex-col"
                                                    button={
                                                        <span className="ico">
                                                            <img
                                                                src={DotsSvg}
                                                                alt=""
                                                            />
                                                        </span>
                                                    }
                                                    menuClassName="dropdown-md"
                                                    menu={
                                                        <DropdownList
                                                            list={[
                                                                {
                                                                    title: 'View Profile',
                                                                    icon: (
                                                                        <ProfileIcon />
                                                                    ),
                                                                    action: () => {
                                                                        navigate(
                                                                            `/tenant-admin-user-management/${user.id}`,
                                                                        )
                                                                    },
                                                                },
                                                                {
                                                                    title: 'Edit',
                                                                    icon: (
                                                                        <EditIcon />
                                                                    ),
                                                                    action: () => {
                                                                        editHandler(
                                                                            user.id,
                                                                        )
                                                                    },
                                                                },
                                                                {
                                                                    title: 'Add to Team',
                                                                    icon: (
                                                                        <TeamIcon />
                                                                    ),
                                                                    action: () => {
                                                                        setEditUserId(
                                                                            String(
                                                                                user.id,
                                                                            ),
                                                                        )
                                                                        setAddUserToTeamModal(
                                                                            true,
                                                                        )
                                                                    },
                                                                },
                                                                {
                                                                    title: 'Add to WorkGroup',
                                                                    icon: (
                                                                        <SocialIcon />
                                                                    ),
                                                                    action: () => {
                                                                        setEditUserId(
                                                                            String(
                                                                                user.id,
                                                                            ),
                                                                        )
                                                                        setAddUserToWorkGroupModal(
                                                                            true,
                                                                        )
                                                                    },
                                                                },
                                                                {
                                                                    title: 'Start Chat',
                                                                    icon: (
                                                                        <ChatIcon />
                                                                    ),
                                                                    action: async () =>
                                                                        startChatHandler(
                                                                            user,
                                                                        ),
                                                                },
                                                                {
                                                                    title: 'Delete',
                                                                    icon: (
                                                                        <DeleteIcon />
                                                                    ),
                                                                    action: () => {
                                                                        deleteHandler(
                                                                            user.id,
                                                                        )
                                                                    },
                                                                    type: 'btn-drp-link-danger',
                                                                },
                                                            ]}
                                                        />
                                                    }
                                                />
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5}>
                                            <h4 className="text-center">
                                                Users not found
                                            </h4>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <Pagination
                    pagination={data?.pagination}
                    onPageChange={() => {}}
                />
            </div>
            {addUserModal && (
                <AddUserModal
                    isOpen={addUserModal}
                    onClose={() => {
                        setAddUserModal(false)
                    }}
                    id={editUserId}
                    setId={setEditUserId}
                />
            )}
            {addUserToTeamModal && (
                <AddToTeam
                    isOpen={addUserToTeamModal}
                    onClose={() => {
                        setAddUserToTeamModal(false)
                    }}
                    userId={editUserId}
                />
            )}
            {addUserToWorkGroupModal && (
                <AddToWorkGroup
                    isOpen={addUserToWorkGroupModal}
                    onClose={() => {
                        setAddUserToWorkGroupModal(false)
                    }}
                    userId={editUserId}
                />
            )}
        </main>
    )
}
